/*
 * Status → Badge variant mapping
 *
 * Single source of truth for every claim, session, note, and client status
 * displayed anywhere in the app. Legacy had this duplicated across 8+ files;
 * here it lives in one place so changing a color is a one-line edit.
 */

export type BadgeVariant =
    | 'success'
    | 'info'
    | 'warning'
    | 'error'
    | 'neutral'
    | 'finalized';

const STATUS_MAP: Record<string, BadgeVariant> = {
    // Claim statuses
    Paid: 'success',
    Ready: 'info',
    Submitted: 'warning',
    Partial: 'info',
    Denied: 'error',
    Voided: 'neutral',
    Adjusted: 'warning',
    // Invoice-only statuses (Draft/Partial/Paid/Voided collide with claims above)
    Issued: 'warning',

    // Session statuses
    Scheduled: 'info',
    Complete: 'success',
    Completed: 'success',
    Canceled: 'error',

    // Note statuses — 'Finalized' gets the special purple variant
    'Not Started': 'neutral',
    Draft: 'warning',
    Drafting: 'warning',
    Missing: 'error',
    Signed: 'success',
    Finalized: 'finalized',

    // Client statuses
    Active: 'success',
    Pending: 'warning',
    Inactive: 'error',
    Expiring: 'warning',
    Expired: 'error',

    // Program/data statuses
    Planned: 'neutral',
    'In Progress': 'info',
    Mastered: 'success',
    'On Hold': 'warning',
    Baseline: 'neutral',
    Discontinued: 'neutral',
    Resolved: 'success',
    Archived: 'neutral',

    // EVV statuses
    Open: 'info',
    Verified: 'success',
    Unverified: 'warning',
    Exception: 'error',
};

export function statusToBadgeVariant(status: string | null | undefined): BadgeVariant {
    if (!status) return 'neutral';
    return STATUS_MAP[status] ?? 'neutral';
}

// ── EDI submission/response variants ──────────────────────────────
// Kept separate from STATUS_MAP because the keys are machine-format
// (`pending_upload`, `ack_accepted`) and the response variant depends on
// both the response type (999 / 277CA / 835) and the per-type status —
// a flat map isn't expressive enough. Both callers used to duplicate
// these switches; routing them through here keeps the palette unified.

export type EdiSubmissionStatus =
    | 'pending_upload'
    | 'uploaded'
    | 'ack_accepted'
    | 'ack_rejected'
    | 'partial_paid'
    | 'paid'
    | 'failed';

export type EdiResponseType = '999' | '277CA' | '835';

export function ediSubmissionVariant(s: EdiSubmissionStatus): BadgeVariant {
    switch (s) {
        case 'paid':
        case 'ack_accepted':  return 'success';
        case 'partial_paid':  return 'warning';
        case 'ack_rejected':
        case 'failed':        return 'error';
        case 'uploaded':      return 'info';
        case 'pending_upload':
        default:              return 'neutral';
    }
}

export function ediResponseVariant(type: EdiResponseType, status: string): BadgeVariant {
    if (type === '999') return status === 'rejected' ? 'error' : 'success';
    if (type === '277CA') {
        if (status === 'rejected') return 'error';
        if (status === 'partial')  return 'warning';
        if (status === 'accepted') return 'success';
        return 'neutral';
    }
    // 835 — always informational; payment details read from other fields.
    return 'info';
}
